import { AsyncPipe, DatePipe } from '@angular/common';
import { Component } from '@angular/core';
import { Router, RouterModule } from '@angular/router';
import { ICellRendererAngularComp } from 'ag-grid-angular';
import { ICellRendererParams } from 'ag-grid-enterprise';
import { Dictionary, RedirectItem } from '../../cross-cutting/models/common';
import { Observable, of, switchMap } from 'rxjs';
import { NgClass } from '@angular/common';

export type RecordCellRenderedParams = ICellRendererParams & {
  relatedToRedirectConfig?: Dictionary<RedirectItem>;
  recordType?: string;
};

type SelectLink = {
  id: string;
  url: Observable<string>;
  label: string;
  deferred?: boolean;
  queryParams?: Dictionary<string>;
};

@Component({
  standalone: true,
  imports: [AsyncPipe, RouterModule, DatePipe, NgClass],
  template: `
  @if (!params?.relatedToRedirectConfig) {
  <div class="select-container flex flex-row gap-2 items-center">
    @for (item of items; track item; let last = $last) {
      <span [ngClass]="{ 'select-item': params?.colDef?.context?.meta?.multi_select }">{{ item }}</span>
    }
    </div>
  } @else {
    @if (params?.value && params?.recordType === 'Engagement') {
      <span class="underline truncate w-25">
        {{ params.value?.engagement_type }} on {{ params.value?.event_date | date: 'MMM dd, y' }}
      </span>
    } @else {
      @for (item of items; track item; let last = $last) {
        @if (item?.deferred) {
          <a (click)="deferredRedirect(item, $event)">{{ item?.label }}</a
          >{{ last ? null : ', ' }}
        } @else {
          <a [routerLink]="item?.url | async" [queryParams]="item?.queryParams">{{ item?.label }}</a
          >{{ last ? null : ', ' }}
        }
      }
    }
  }
  `,
  styles: [
    `
      a {
        text-decoration: underline;
        cursor: pointer;
      }
      a:hover {
        color: var(--primary-color);
      }
      .select-container {
        padding-top: var(--select-container-top-padding);
        padding-bottom: var(--select-container-bottom-padding);
        flex-wrap:nowrap; 
        overflow:hidden;
        mask-image: linear-gradient(90deg, #000 85%, transparent);
        height: 100%;
      }
      .select-item {
        background: transparent;
        color: inherit;
        border: 1px solid currentColor;
        padding: 2px 8px;
        border-radius: 20px;
        font-size: 12px;
        line-height: 1.2;
        display: inline-flex;
        align-items: center;
      }
    `,
  ],
})
export class TsAgMultiSelectRenderer implements ICellRendererAngularComp {
  items: any[] = [];
  params: RecordCellRenderedParams;

  constructor(private readonly router: Router) {}

  agInit(params: RecordCellRenderedParams): void {
    this.params = params;
    this.items = this.getItems(params.value);
  }

  refresh(params: RecordCellRenderedParams): boolean {
    this.params = params;
    this.items = this.getItems(params.value);
    return true;
  }

  deferredRedirect(item: SelectLink, event: MouseEvent): void {
    item.url.subscribe((url) => {
      if (event.ctrlKey || event.metaKey) {
        window.open(url, '_blank');
      } else {
        this.router.navigateByUrl(url);
      }
    });
  }

  private getItems(value: any): any[] {
    if (this.params.relatedToRedirectConfig) {
      if (Array.isArray(value)) {
        return value.map((item) => this.recordStringToLink(item));
      }
      if (typeof value === 'string') {
        return [this.recordStringToLink(value)];
      }
      return [];
    } else {
      const options = this.params.colDef?.cellEditorParams?.options || [];
      if (Array.isArray(value)) {
        return value.map((id) => {
          const opt = options.find(option => String(option.id) === String(id));
          return opt?.label ?? String(id);
        });
      }
      if (typeof value === 'string' || typeof value === 'number') {
        if (this.params.valueFormatted !== undefined && this.params.valueFormatted !== null && this.params.valueFormatted !== '') {
          return [this.params.valueFormatted];
        }
        const opt = options.find(option => String(option.id) === String(value));
        return [opt?.label ?? String(value)];
      }
      return [];
    }
  }

  private recordStringToLink(recordString: string): SelectLink {
    const [id, label] = [
      recordString.substring(0, recordString.indexOf('|')),
      recordString.substring(recordString.indexOf('|') + 1),
    ];

    const redirectConfig = this.params.relatedToRedirectConfig[`${this.params.recordType}`];
    let url: Observable<string> = null;

    if (redirectConfig) {
      url = redirectConfig.route(Number(id)).pipe(switchMap((pattern) => of(pattern.replace(':id', id))));
    }

    return {
      id,
      url,
      label,
      deferred: redirectConfig?.deferred,
      queryParams: redirectConfig?.getQueryParams?.(Number(id)),
    };
  }
}
